import type { ApprovalRunOrigin } from '../models/insightAction.types'

import type { ApprovalInboxRow } from '@/features/platform-actions'

/** The requests of one insight run that landed on this page of the approvals queue. */
export interface ApprovalRunGroup {
  runId: string
  /** The first request's origin — the run's title, config and insight are the same on every one. */
  origin: ApprovalRunOrigin
  rows: ApprovalInboxRow[]
  /** The run's own awaiting count, so a card is right about a page boundary it cannot see. */
  pendingTotal: number
  /** request id → the `item_ids[]` value the bulk decision endpoints take. */
  itemIds: Record<string, number>
  /** request id → that request's own row summary (each request is a different row of the run). */
  summaries: Record<string, string>
}

/** The queue in its own order: a run's card where its first request sat. */
export type ApprovalQueueEntry =
  | { kind: 'group'; key: string; group: ApprovalRunGroup }
  | { kind: 'row'; key: string; row: ApprovalInboxRow }

export interface ApprovalQueue {
  entries: ApprovalQueueEntry[]
  groups: ApprovalRunGroup[]
  loose: ApprovalInboxRow[]
}

export type ApprovalOrigins = Readonly<Record<string, ApprovalRunOrigin | null | undefined>>

/**
 * Only a request still waiting on a decision folds into a run card: the card's verbs are approve
 * and reject, and a closed or confirm-pending request keeps its own row, status and verbs.
 */
function groupable(row: ApprovalInboxRow, origins: ApprovalOrigins): ApprovalRunOrigin | null {
  if (row.approvalState !== 'awaiting_decision') return null
  return origins[row.request.id] ?? null
}

/**
 * Fold the requests of one insight run into a single group. The queue is ordered, and grouping
 * must not reorder it: a group sits where its first request sat, and everything else keeps its
 * place around it. A request no insight opened is left exactly as it was.
 */
export function groupApprovalRows(
  rows: readonly ApprovalInboxRow[],
  origins: ApprovalOrigins,
): ApprovalQueue {
  const byRun = new Map<string, ApprovalRunGroup>()
  const entries: ApprovalQueueEntry[] = []
  const loose: ApprovalInboxRow[] = []

  for (const row of rows) {
    const id = row.request.id
    const origin = groupable(row, origins)
    if (!origin) {
      loose.push(row)
      entries.push({ kind: 'row', key: id, row })
      continue
    }
    const existing = byRun.get(origin.runId)
    if (existing) {
      existing.rows.push(row)
      existing.itemIds[id] = origin.itemId
      existing.summaries[id] = origin.rowSummary
      continue
    }
    const group: ApprovalRunGroup = {
      runId: origin.runId,
      origin,
      rows: [row],
      pendingTotal: origin.pendingTotal,
      itemIds: { [id]: origin.itemId },
      summaries: { [id]: origin.rowSummary },
    }
    byRun.set(origin.runId, group)
    entries.push({ kind: 'group', key: origin.runId, group })
  }

  // A run whose count the server left out still has at least the rows this page shows.
  const groups = [...byRun.values()]
  for (const group of groups) {
    group.pendingTotal = Math.max(group.pendingTotal, group.rows.length)
  }
  return { entries, groups, loose }
}

/** The bulk endpoints take item ids, not request ids; a request the lookup did not map is dropped. */
export function itemIdsFor(group: ApprovalRunGroup, requestIds: Iterable<string>): number[] {
  return [...requestIds]
    .map((id) => group.itemIds[id])
    .filter((id): id is number => typeof id === 'number')
}
